import Api from './Api';
import API_ROUTES from '../constants/apiRoutes.constants';

// Solo legacy providers (practitioner-scoped) for the admin Migration page. They
// have no user_group, so they use a parallel list + export path in ellamd-api.
export interface ILegacyMigrationPractitioner {
  id: number;
  name: string | null;
  ownerFullName: string | null;
  ownerEmail: string | null;
  // Set only after the FINAL re-migration. migratedAt is the cutover switch, not a label: it puts
  // the old EHR in view-only mode for this provider, routes their next login to /migration-choice
  // and redirects their patients to the new portal (ellamd-api Identity#migrated?).
  migratedAt: string | null;
  // The new EHR's business id OR hashid ("bK7umB") -- its admin surfaces show the hashid, so that
  // is what operators paste. Stored and echoed verbatim; never parsed as a number.
  externalBusinessId: string | null;
}

const LegacyMigration = {
  async getPractitioners(search?: string): Promise<{
    success: boolean;
    data: ILegacyMigrationPractitioner[];
  }> {
    const query = search ? `?q=${encodeURIComponent(search)}` : '';
    return Api.get(`${API_ROUTES.LEGACY_MIGRATION_PRACTITIONERS}${query}`);
  },

  // mergePractitionerIds: sub-providers whose practices are becoming ONE new-EHR business with
  // this practitioner as owner (Brandi Bowers + Mandy Ward). ellamd-api folds their data into the
  // owner's export — deduped by old-EHR id, owner-wins on pricing. Empty = a normal solo export.
  async startExportJob(practitionerId: number, mergePractitionerIds: number[] = []): Promise<{
    success: boolean;
    data: {
      jobId: number;
      status: string;
      createdAt: string;
      expiresAt: string;
    };
  }> {
    return Api.post(API_ROUTES.EXPORT_PRACTITIONER_DATA(practitionerId), {
      merge_practitioner_ids: mergePractitionerIds,
    });
  },

  async getExportStatus(practitionerId: number, jobId: number): Promise<{
    success: boolean;
    data: {
      jobId: number;
      status: string;
      s3Url: string | null;
      errorMessage: string | null;
      startedAt: string | null;
      completedAt: string | null;
      expiresAt: string;
      createdAt: string;
    };
  }> {
    return Api.get(API_ROUTES.EXPORT_PRACTITIONER_STATUS(practitionerId, jobId));
  },

  // The practitioner-scoped parallel to UserGroups.updateMigrationStatus. Unlike that one, the
  // server stamps migrated_at itself, so we send the intent (`migrated`) rather than a timestamp.
  async updateMigrationStatus(
    practitionerId: number,
    migrated: boolean,
    externalBusinessId: string | null
  ): Promise<void> {
    await Api.post(API_ROUTES.LEGACY_MIGRATION_PRACTITIONER_STATUS(practitionerId), {
      migrated,
      external_business_id: migrated ? externalBusinessId : null,
    });
  },
};

export default LegacyMigration;
